<template>
    <div class="receipt-container">
        <div class="receipt">
            <!-- Receipt Header -->
            <div class="receipt-header">
                <div class="store-logo">
                    <h1>🛒 LGU Palompon</h1>
                    <div class="store-info">
                        <p>Rizal St., Palompo, Leyte</p>
                        <p>06538, Philippines</p>
                        <p>palomponleyte.gov.ph</p>
                    </div>
                </div>

                <div class="transaction-info">
                    <p>BILL #: {{ soa.bsoa_id?.toString().padStart(8, '0') }}</p>
                    <p>DATE: {{ currentDate }}</p>
                    <p>TIME: {{ currentTime }}</p>
                    <p>DUE: {{ formatReceiptDate.compact(soa.due_date) }}</p>
                </div>
            </div>

            <div class="divider">================================</div>

            <!-- Column Headers -->
            <div class="column-headers">
                <div class="col-item">ITEM</div>
                <div class="col-qty">QTY</div>
                <div class="col-price">PRICE</div>
            </div>

            <div class="divider thin">--------------------------------</div>

            <!-- Items List -->
            <div class="items-list">
                <div v-for="(item, index) in soa.details" :key="index" class="item-row">
                    <div class="col-item">
                        <span class="item-name">{{ item.fee }}</span>
                        <span v-if="item.ltp_id===1838" class="item-discount">* MANUALLY ADDED *</span>
                    </div>
                    <div class="col-qty">1</div>
                    <div class="col-price">{{ formatPrice(item.amount) }}</div>
                </div>
            </div>

            <div class="divider thin">--------------------------------</div>

            <!-- Totals -->
            <div class="totals-section">
                <div class="total-row grand-total">
                    <span>TOTAL:</span>
                    <span>{{ formatPrice(calculateTotal()) }}</span>
                </div>
            </div>

            <div class="divider thin">--------------------------------</div>

            <!-- Footer -->
            <div class="receipt-footer">
                <p>Powered By: Exodous I.T. Solutions</p>
                <p>&copy; 2025 LGU Palompon.</p>
                <p>All rights reserved.</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
//eslint-disable-next-line
const props = defineProps({
    soa: {
        type: Object,
        //eslint-disable-next-line
        default: () => { details: [] }
    }
});

// Receipt data

// Get current date and time
const now = new Date();
const currentDate = computed(() => {
    return now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    });
});

const formatReceiptDate = {
    // Format: "JAN 20, 2025" (uppercase, compact)
    receiptHeader: (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
        const day = date.getDate();
        const year = date.getFullYear();

        return `${month} ${day}, ${year}`;
    },

    // Format: "01/20/25" (short year, numeric)
    compact: (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);

        return `${month}/${day}/${year}`;
    },

    // Format: "2025-01-20 12:00 AM"
    withTime24: (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    // Format: "Monday, January 20, 2025"
    verbose: (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }
};

const currentTime = computed(() => {
    return now.toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit'
    }).toLowerCase();
});

const formatPrice = (price) => {
    if (typeof price === 'number') {
        return '₱' + price.toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    // Handle string inputs
    if (typeof price === 'string') {
        const num = parseFloat(price);
        if (!isNaN(num)) {
            return '₱' + num.toLocaleString('en-PH', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
    }

    return '₱0.00';
};



const calculateTotal = () => {
    if (!props.soa || !props.soa.details) return 0;
    return props.soa.details.reduce((sum, item) => sum + (item.amount || 0), 0);
};

</script>

<style scoped>
.receipt-container {
    font-family: 'Courier New', Courier, monospace;
    background-color: #f5f5f5;
    padding: 20px;
    display: flex;
    justify-content: center;
    min-height: 100vh;
}

.receipt {
    background-color: white;
    width: 280px;
    padding: 20px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    border: 1px solid #ddd;
    font-size: 12px;
    line-height: 1.2;
    letter-spacing: -0.5px;
}

/* Header Styles */
.receipt-header {
    text-align: center;
    margin-bottom: 10px;
}

.store-logo h1 {
    font-size: 20px;
    font-weight: bold;
    margin: 5px 0;
    letter-spacing: 1px;
}

.store-info {
    font-size: 10px;
    margin-bottom: 10px;
    line-height: 1.4;
}

.store-info p {
    margin: 2px 0;
}

.transaction-info {
    text-align: left;
    font-size: 10px;
    margin-bottom: 10px;
}

.transaction-info p {
    margin: 2px 0;
}

/* Column Headers */
.column-headers {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    padding: 5px 0;
    border-bottom: 1px dashed #000;
    margin-bottom: 5px;
}

.col-item {
    flex: 3;
    text-align: left;
    padding-left: 2px;
}

.col-qty {
    flex: 1;
    text-align: center;
}

.col-price {
    flex: 2;
    text-align: right;
    padding-right: 2px;
}

/* Items List */
.items-list {
    margin: 5px 0;
}

.item-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px dotted #eee;
}

.item-row:last-child {
    border-bottom: none;
}

.item-name {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.item-discount {
    font-size: 9px;
    color: #d9534f;
    font-weight: bold;
    display: block;
    margin-top: 1px;
}

/* Totals Section */
.totals-section {
    margin: 10px 0;
}

.total-row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
}

.total-row.discount {
    color: #d9534f;
}

.total-row.grand-total {
    font-weight: bold;
    font-size: 14px;
    padding: 5px 0;
    border-top: 1px solid #000;
    margin-top: 5px;
}

/* Payment Info */
.payment-info {
    margin: 10px 0;
}

.payment-row {
    display: flex;
    justify-content: space-between;
    padding: 2px 0;
}

/* Footer */
.receipt-footer {
    text-align: center;
    font-size: 10px;
    margin-top: 10px;
}

.receipt-footer p {
    margin: 4px 0;
}

.barcode {
    font-family: 'Libre Barcode 128', cursive;
    font-size: 24px;
    letter-spacing: 2px;
    margin: 10px 0;
}

/* Dividers */
.divider {
    text-align: center;
    margin: 10px 0;
    font-size: 11px;
    color: #333;
}

.divider.thin {
    margin: 5px 0;
}

/* Print styles */
@media print {
    .receipt-container {
        padding: 0;
        background: white;
    }

    .receipt {
        box-shadow: none;
        border: none;
        width: 100%;
        padding: 10px;
    }
}
</style>